import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

import "../styles/generalStyle.css";
import "./Letras.css";
import { useLanguage } from "../i18n/LanguageContext";

function twoLinePreview(text = "") {
  const t = (text || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  return t.length > 140 ? t.slice(0, 140) + "…" : t;
}

export default function LetrasListPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!firebaseUser) {
      setLoading(false);
      setItems([]);
      return;
    }

    setLoading(true);
    setError("");

    const letrasRef = collection(db, "usuarios", firebaseUser.uid, "letras");
    const q = query(letrasRef, orderBy("fechaCreacion", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        

        const arr = snap.docs.map((d) => ({ ...d.data(), id: d.id, docId: d.id }));

        setItems(arr);
        setLoading(false);
      },
      (e) => {
        console.error("Error leyendo letras:", e);
        setError(t("letras.list.errorLoad"));
        setLoading(false);
      }
    );

    return () => unsub();
  }, [firebaseUser, t]);

  const hasItems = useMemo(() => items.length > 0, [items]);

  return (
    <div className="page">
      <div className="container">
        {/* Header */}
        <div className="letrasHeader">
          <div>
            <h1 className="h1 letrasTitle">
              <span className="letrasTitleIcon" aria-hidden>📝</span>
              {t("letras.list.title")}
            </h1>
            <p className="p-muted" style={{ marginTop: 6 }}>
              {t("letras.list.subtitle")}
            </p>
          </div>

          <button
            className="btn btn-primary"
            onClick={() => navigate("/app/crear-letra")}
          >
            {t("letras.list.createButton")}
          </button>
        </div>

        {/* State */}
        {loading ? (
          <div className="card">
            <div className="profile-loading" style={{ marginTop: 0 }}>
              <div className="spinner" />
              <p>{t("letras.list.loading")}</p>
            </div>
          </div>
        ) : error ? (
          <div className="card">
            <div className="alert-error">{error}</div>
          </div>
        ) : !hasItems ? (
          <div className="card">
            <div className="emptyBox">
              <div className="emptyIcon" aria-hidden>✨</div>
              <div className="emptyTitle">{t("letras.list.emptyTitle")}</div>
              <div className="emptyDesc">{t("letras.list.emptyDesc")}</div>

              <button
                className="btn btn-primary"
                style={{ marginTop: 12 }}
                onClick={() => navigate("/app/crear-letra")}
              >
                {t("letras.list.emptyCta")}
              </button>
            </div>
          </div>
        ) : (
          <div className="letrasList">
            {items.map((it) => {
              const titulo = it.titulo?.trim() || t("letras.item.untitled");
              const estilo = it.estilo?.trim() || "";
              const preview = twoLinePreview(it.letra || "");

              return (
                <button
                  key={it.id}
                  className="letraRow"
                  onClick={() => navigate(`/app/letras/${it.id}`)}
                  type="button"
                >
                  <div className="letraRowLeft">
                    <div className="letraRowIcon" aria-hidden>🎶</div>
                    <div className="letraRowText">
                      <div className="letraRowTitle">{titulo}</div>

                      <div className="letraRowMeta">
                        <span className="metaChip">
                          {t("letras.item.metaStyle")}
                          <b className="metaChipValue">
                            {estilo ? estilo : "—"}
                          </b>
                        </span>
                      </div>

                      {preview ? (
                        <div className="letraRowPreview">{preview}</div>
                      ) : (
                        <div className="letraRowPreview muted">
                          {t("letras.item.noPreview")}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="letraRowRight" aria-hidden>›</div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
